import React from "react";
import styled from "styled-components";
import CalendarTodayIcon from "@material-ui/icons/CalendarToday";
import CloseIcon from "@material-ui/icons/Close";
import moment from "moment";
import { DateTimePicker } from "@sonder/ui/source/styled/components/DateTimePicker";
import { FooterSection } from "@sonder/ui/source/styled/components/DateTimePicker/Footer";
import { Dropdown } from "@sonder/ui/source/styled/components/Dropdown";
import { DropdownContent } from "@sonder/ui/source/styled/components/DropdownContent";
import { SearchDropdownTrigger } from "@sonder/ui/source/styled/fragments/SearchDropdownTrigger";
import { borders, colors } from "@sonder/ui/source/styled/theme";
import { updateEndDate, updateStartDate } from "search/actions";
import { useSearchStore } from "search/store";

const Wrapper = styled.div`
  border-radius: 0.285rem;
  border: ${borders.standard};
  margin-top: 0.714rem;

  & button {
    border: none;
  }

  & > div:first-child > button {
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  }

  & > div:last-child > button {
    border-top-left-radius: 0;
    border-top-right-radius: 0;
  }
`;

const StyledDropdown = styled(Dropdown)`
  width: 100%;
`;

const StyledDateTimePicker = styled(DateTimePicker)`
  & ${FooterSection} {
    display: none;
  }
`;

const Divider = styled.div`
  background-color: ${colors.grayE};
  height: 1px;
  width: 100%;
`;

const StyledCloseIcon = styled(CloseIcon)`
  color: ${colors.gray1};
`;

const StyledCalendarTodayIcon = styled(CalendarTodayIcon)`
  color: ${colors.grayE};
`;

const createTrigger = (
  date: moment.Moment | null,
  startOrEndDate: "startDate" | "endDate",
) => {
  const { dispatch } = useSearchStore();
  const startOrEndText = startOrEndDate === "startDate" ? "Start" : "End";

  return (
    <SearchDropdownTrigger iconRight iconSize="1.714rem">
      <div>{date ? date.format("MMM D, YYYY") : startOrEndText}</div>
      {date ? (
        <StyledCloseIcon
          onClick={e => {
            e.stopPropagation();
            if (startOrEndDate === "startDate") {
              dispatch(updateStartDate(null));
            } else {
              dispatch(updateEndDate(null));
            }
          }}
        />
      ) : (
        <StyledCalendarTodayIcon />
      )}
    </SearchDropdownTrigger>
  );
};

export const SearchDateFilter = () => {
  const { state, dispatch } = useSearchStore();
  const { startDate, endDate } = state;

  return (
    <Wrapper>
      <StyledDropdown trigger={createTrigger(state.startDate, "startDate")}>
        {({ isOpen, closePortal }) =>
          isOpen && (
            <DropdownContent>
              <StyledDateTimePicker
                date={startDate}
                isOutsideRange={day => {
                  if (state.endDate) {
                    return day.isAfter(state.endDate);
                  }
                  return day.isAfter(moment().startOf("day"));
                }}
                onDateChange={nextDate => {
                  if (nextDate) {
                    dispatch(updateStartDate(nextDate.startOf("day")));
                    closePortal();
                  }
                }}
              />
            </DropdownContent>
          )
        }
      </StyledDropdown>

      <Divider />

      <StyledDropdown trigger={createTrigger(state.endDate, "endDate")}>
        {({ isOpen, closePortal }) =>
          isOpen && (
            <DropdownContent>
              <StyledDateTimePicker
                date={endDate}
                isOutsideRange={day => {
                  if (state.startDate) {
                    return (
                      day.isBefore(state.startDate) ||
                      day.isAfter(moment().endOf("day"))
                    );
                  }
                  return day.isAfter(moment().endOf("day"));
                }}
                onDateChange={nextDate => {
                  if (nextDate) {
                    dispatch(updateEndDate(nextDate.endOf("day")));
                    closePortal();
                  }
                }}
              />
            </DropdownContent>
          )
        }
      </StyledDropdown>
    </Wrapper>
  );
};
